import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { DashboardHeader } from '@/src/modules/components/Header';
import { Icons } from '@/src/modules/components/Icons';
import { PromptBox } from '@/src/modules/components/ui/chatgpt-prompt-input';
import { cn } from '@/src/infrastructure/lib/utils';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { sendSupportMessage, type SupportChatMessage } from '@/src/infrastructure/lib/api';
import { Link } from 'react-router-dom';

function renderMessageContent(content: string) {
  const target = 'Please contact help & support';
  if (content.includes(target)) {
    const parts = content.split(target);
    return (
      <>
        {parts[0]}
        Please contact{' '}
        <Link
          to="/dashboard/help"
          className="underline font-semibold text-primary hover:text-brand-deep"
        >
          Help & Support
        </Link>
        {parts[1]}
      </>
    );
  }
  return content;
}

const SUGGESTIONS: { icon: any; text: string }[] = [
  { icon: Icons.Rocket, text: 'How do I publish my invitation?' },
  { icon: Icons.Users, text: 'How does RSVP work?' },
  { icon: Icons.Crown, text: "What's included in the PRO plan?" },
  { icon: Icons.Templates, text: 'Can I change my template after creating an invitation?' },
];

/** Animated three-dot "assistant is typing" indicator. */
function TypingDots() {
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-primary/60"
          animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

function AssistantAvatar() {
  return (
    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-brand-deep text-white shadow-sm">
      <Icons.Sparkles size={15} />
    </span>
  );
}

export function Support() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<SupportChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  // Bumped after every send so the uncontrolled PromptBox remounts and clears.
  const [formKey, setFormKey] = useState(0);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, sending]);

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    const nextMessages: SupportChatMessage[] = [
      ...messages,
      { role: 'user', content: trimmed },
    ];
    setMessages(nextMessages);
    setSending(true);
    setError('');
    setFormKey((k) => k + 1);

    try {
      const { reply } = await sendSupportMessage(nextMessages);
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to reach support chat.',
      );
    } finally {
      setSending(false);
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    send(String(data.get('message') ?? ''));
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter sends; Shift+Enter inserts a newline.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  }

  const isEmpty = messages.length === 0;
  const userInitial = (user?.displayName?.[0] ?? user?.email?.[0] ?? 'U').toUpperCase();

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-surface-lowest">
      <DashboardHeader
        title="Support"
        description="Chat with the InviteCloud assistant — templates, publishing, RSVP and billing."
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Conversation */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-8">
          <div className="mx-auto w-full max-w-2xl py-8">
            {isEmpty ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="flex flex-col items-center gap-5 pt-8 text-center"
              >
                <div className="relative">
                  <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-brand-deep text-white shadow-lg shadow-primary/20">
                    <Icons.Sparkles size={28} />
                  </span>
                  <motion.span
                    className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-status-published ring-2 ring-surface-lowest"
                    animate={{ scale: [1, 1.25, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-on-surface">
                    How can we help
                    {user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ''}?
                  </h2>
                  <p className="mx-auto mt-1.5 max-w-md text-sm text-on-surface-variant">
                    Ask anything about creating, publishing or sharing your
                    invitations — or start with a popular question below.
                  </p>
                </div>
                <div className="grid w-full gap-3 sm:grid-cols-2">
                  {SUGGESTIONS.map((s, i) => (
                    <motion.button
                      key={s.text}
                      type="button"
                      onClick={() => send(s.text)}
                      disabled={sending}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.06 }}
                      whileHover={{ y: -3 }}
                      className="group flex items-start gap-3 rounded-2xl border border-outline-variant/40 bg-surface-lowest p-4 text-left shadow-sm transition-all hover:border-primary/40 hover:shadow-md disabled:opacity-50"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                        <s.icon size={16} />
                      </span>
                      <span className="text-sm font-medium text-on-surface">
                        {s.text}
                      </span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <div className="flex flex-col gap-5">
                <AnimatePresence initial={false}>
                  {messages.map((message, index) => {
                    const isUser = message.role === 'user';
                    return (
                      <motion.div
                        key={index}
                        layout
                        initial={{ opacity: 0, y: 12, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 26 }}
                        className={cn(
                          'flex items-end gap-2.5',
                          isUser ? 'justify-end' : 'justify-start',
                        )}
                      >
                        {!isUser && <AssistantAvatar />}
                        <div
                          className={cn(
                            'max-w-[80%] whitespace-pre-wrap px-4 py-2.5 text-sm shadow-sm',
                            isUser
                              ? 'rounded-2xl rounded-br-md bg-primary text-primary-foreground'
                              : 'rounded-2xl rounded-bl-md border border-outline-variant/40 bg-surface-lowest text-on-surface',
                          )}
                        >
                          {renderMessageContent(message.content)}
                        </div>
                        {isUser && (
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-low text-xs font-bold text-on-surface-variant">
                            {userInitial}
                          </span>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
                {sending && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-end gap-2.5"
                  >
                    <AssistantAvatar />
                    <div className="rounded-2xl rounded-bl-md border border-outline-variant/40 bg-surface-lowest px-4 py-3.5 shadow-sm">
                      <TypingDots />
                    </div>
                  </motion.div>
                )}
              </div>
            )}

            {error && (
              <div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-status-failed/10 px-4 py-2.5 text-sm font-medium text-status-failed">
                <Icons.Warning size={15} />
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        <div className="bg-surface-lowest/80 px-4 pb-4 pt-2 backdrop-blur-md md:px-8">
          <form
            key={formKey}
            onSubmit={handleSubmit}
            className="mx-auto w-full max-w-2xl"
          >
            <PromptBox
              name="message"
              onKeyDown={handleKeyDown}
              disabled={sending}
              aria-label="Message support"
            />
            <p className="mt-2 text-center text-xs text-on-surface-variant">
              The assistant can make mistakes — verify important details.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
